import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Autocomplete, Box, Button, TextField, Typography,
  Grid2 as Grid, Checkbox, FormControlLabel, Collapse, Divider,
  IconButton
} from "@mui/material";
import Icons from "../../../assets/js/Icons";
import { handleRetrieveSpecializations } from "../../../redux/public-slices/specialistappointment.slice";
import { identityList, languagesList } from "./data";
import { useToast } from "../../../hooks/useToast";



const SpecialistSearchBanner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [specializations, setSpecializations] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [zipCode, setZipCode] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedIdentities, setSelectedIdentities] = useState([]);

  const handleGetAllSpecializations = () => {
    dispatch(handleRetrieveSpecializations({ limit: 100 }))
      .unwrap()
      .then((data) => {
        setSpecializations(data.specializations || []);
      })
      .catch(() => { });
  };
  const handleZipChange = (e) => {
    const value = e.target.value;

    if (!/^\d*$/.test(value)) {
      return;
    }

    setZipCode(value);


  };


  useEffect(() => {
    handleGetAllSpecializations();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const search = searchParams.get("search");
    const zip = searchParams.get("zip");
    const lang = searchParams.get("language");
    const identity = searchParams.get("identity");

    if (search && specializations.length > 0) {
      const found = specializations.find(s => s.name === search);
      setSelectedTopic(found || { name: search });
    }

    if (zip) setZipCode(zip);
    if (lang) setSelectedLanguages(lang.split(","));
    if (identity) setSelectedIdentities(identity.split(","));
  }, [searchParams, specializations]);

  const handleToggleFilter = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const onSearch = () => {
    const hasSearch = selectedTopic?.name || zipCode || selectedLanguages?.length > 0 || selectedIdentities?.length > 0;
    if (!hasSearch) {
      toast.error("Please select at least one search criteria");
      return;
    }
    const params = new URLSearchParams();
    if (selectedTopic?.name) params.append("search", selectedTopic.name);
    if (zipCode) params.append("zip", zipCode);
    if (selectedLanguages.length > 0) params.append("language", selectedLanguages.join(","));
    if (selectedIdentities.length > 0) params.append("identity", selectedIdentities.join(","));

    navigate(`/all-specialists?${params.toString()}`);
  };

  return (
    <Grid backgroundColor="other.color1"
      sx={{
        py: 6,
        px: { xs: "5%", lg: "16%" },
      }} container alignItems="center">
      <Grid size={{ xs: 12 }}>
        <Box textAlign={"center"} sx={{ color: 'white' }}>
          {/* Headline Section */}
          <Typography sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', mb: 1, color: '#768FA2' }}>
            Hey There!
          </Typography>
          <Typography sx={{ fontSize: { sm: '36px', lg: '48px' }, fontWeight: 700, mb: 1 }}>
            You're <span style={{ color: '#768FA2' }}>Not Alone</span> on This Journey
          </Typography>
          <Typography sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: 400, lineHeight: 1.6, mb: 4, opacity: 0.9 }}>
            Connect with others, share experiences, and find encouragement in a safe, supportive space.
          </Typography>

          {/* Search Bar Container */}
          <Grid sx={{ flexWrap: { xs: 'wrap', md: 'nowrap', my: 2 } }} container justifyContent="center" spacing={2} >
            <Box sx={{
              width: '100%', maxWidth: '750px',
              display: "flex", flexDirection: { xs: "column", md: "row" },
              alignItems: "center", gap: 1, background: "white", p: 1,
              borderRadius: { xs: "12px", md: "50px" },
            }}>
              <Autocomplete
                size="small"
                options={specializations}
                getOptionLabel={(option) => option?.name || ""}
                value={selectedTopic}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                onChange={(e, val) => setSelectedTopic(val)}
                sx={{ flex: 2, width: "100%", "& fieldset": { border: "none" } }}
                renderInput={(params) => <TextField {...params} placeholder="Select specialty" />}
              />
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 1 }} />
              <Box sx={{ flex: 1, width: "100%" }}>
                <TextField
                  placeholder="Zip code"
                  value={zipCode}
                  onChange={handleZipChange}
                 
                  sx={{
                    "& fieldset": { border: "none !important" },
                    width: "100%",
                  }}
                />

              </Box>

              <IconButton
                size="large"

                sx={{
                  display: { xs: 'none', md: "block", },
                  height: '100%',
                  aspectRatio: 1,
                  backgroundColor: "#2F4250 !important",
                  color: 'white',

                }}
                onClick={() => setShowFilters(!showFilters)}>
                <Icons.Filter />
              </IconButton>


            </Box>

            <IconButton
              size="large"

              sx={{
                display: { xs: 'block', md: "none", },
                backgroundColor: "#ffffffff !important",
                color: 'dark.main',
                height: '100%',
                aspectRatio: 1,


              }}
              onClick={() => setShowFilters(!showFilters)}>
              <Icons.Filter />
            </IconButton>
            <Button
              variant="contained" onClick={onSearch} sx={{
                backgroundColor: "white",
                color: "dark.main",
                fontSize: 20, px: 4, py: 0.5,
                borderRadius: '50px',
                flexGrow: { xs: 1, md: 0 },
                "&.Mui-disabled": { backgroundColor: " rgba(255, 224, 224, 0.14) !important" }

              }}>
              Search
            </Button>

          </Grid>

          {/* Expandable Filters */}
          <Collapse in={showFilters}>
            <Box sx={{
              mt: 2, p: 3, background: "rgba(255,255,255,0.05)",
              borderRadius: "15px", textAlign: "left", maxWidth: "900px", mx: "auto",
              border: "1px solid rgba(255,255,255,0.1)"
            }}>
              <Grid container spacing={4}>
                <Grid size={{ xs: 6, sm: 6 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: "#768FA2" }}>Language</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {languagesList.map((lang) => (
                      <FormControlLabel
                        key={lang}
                        control={<Checkbox size="small" sx={{ color: 'white' }} checked={selectedLanguages.includes(lang)} onChange={() => handleToggleFilter(selectedLanguages, setSelectedLanguages, lang)} />}
                        label={lang}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 6 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: "#768FA2" }}>Therapist Identity</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {identityList.map((id) => (
                      <FormControlLabel
                        key={id}
                        control={<Checkbox size="small" sx={{ color: 'white' }} checked={selectedIdentities.includes(id)} onChange={() => handleToggleFilter(selectedIdentities, setSelectedIdentities, id)} />}
                        label={id}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SpecialistSearchBanner;